// Local asymmetric signing for SignalLink receipts (WebCrypto).
// SECURITY MODEL:
//   * The private key is a NON-EXTRACTABLE CryptoKey persisted in IndexedDB.
//   * localStorage holds only a PUBLIC-ONLY descriptor (public_jwk + id).
//   * No private-key material (JWK 'd', PKCS#8, raw bytes) is ever written to
//     localStorage, receipts, PDFs, logs, or the network.
// A local signature is NOT a government certificate, notarization, blockchain
// confirmation, RFC 3161 timestamp, or external identity verification.

import { sha256Hex } from "./crypto";

let cachedAlgo = null;
let memoryKey = null; // session-only fallback if IndexedDB is unavailable

async function detectAlgo() {
  if (cachedAlgo) return cachedAlgo;
  try {
    await crypto.subtle.generateKey({ name: "Ed25519" }, true, ["sign", "verify"]);
    cachedAlgo = "Ed25519";
  } catch {
    cachedAlgo = "ECDSA-P256";
  }
  return cachedAlgo;
}

function keyParams(algorithm) {
  return algorithm === "Ed25519" ? { name: "Ed25519" } : { name: "ECDSA", namedCurve: "P-256" };
}
function signParams(algorithm) {
  return algorithm === "Ed25519" ? { name: "Ed25519" } : { name: "ECDSA", hash: "SHA-256" };
}

function toB64(buf) {
  const bytes = new Uint8Array(buf);
  let s = "";
  for (let i = 0; i < bytes.length; i++) s += String.fromCharCode(bytes[i]);
  return btoa(s);
}
function fromB64(str) {
  return Uint8Array.from(atob(str), (c) => c.charCodeAt(0));
}

async function thumbprint(jwk) {
  const s = JSON.stringify({ crv: jwk.crv, kty: jwk.kty, x: jwk.x || "", y: jwk.y || "" });
  const h = await sha256Hex(s);
  return h.slice(0, 16).toUpperCase();
}

// ===== IndexedDB key store (stores an opaque, non-extractable CryptoKey) =====
const DB_NAME = "signallink-keys";
const STORE = "keys";
const KEY_ID = "active-signing-private";

function idbAvailable() {
  return typeof indexedDB !== "undefined" && indexedDB !== null;
}
function idbOpen() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => req.result.createObjectStore(STORE);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}
async function idbPut(value) {
  if (!idbAvailable()) {
    memoryKey = value;
    return true;
  }
  const db = await idbOpen();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, "readwrite");
    tx.objectStore(STORE).put(value, KEY_ID);
    tx.oncomplete = () => resolve(true);
    tx.onerror = () => reject(tx.error);
  });
}
async function idbGet() {
  if (!idbAvailable()) return memoryKey;
  const db = await idbOpen();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, "readonly");
    const rq = tx.objectStore(STORE).get(KEY_ID);
    rq.onsuccess = () => resolve(rq.result || null);
    rq.onerror = () => reject(rq.error);
  });
}
async function idbDelete() {
  memoryKey = null;
  if (!idbAvailable()) return true;
  const db = await idbOpen();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, "readwrite");
    tx.objectStore(STORE).delete(KEY_ID);
    tx.oncomplete = () => resolve(true);
    tx.onerror = () => reject(tx.error);
  });
}

async function importPubKey(jwk, algorithm) {
  return crypto.subtle.importKey("jwk", jwk, keyParams(algorithm), false, ["verify"]);
}
// Re-import a private JWK as a NON-EXTRACTABLE signing key (extractable=false).
async function importNonExtractablePrivate(jwk, algorithm) {
  return crypto.subtle.importKey("jwk", jwk, keyParams(algorithm), false, ["sign"]);
}

// Public-only descriptor persisted in localStorage.
function makeDescriptor(label, algorithm, public_jwk, public_key_id) {
  return { label: label || "SignalLink Operator", algorithm, public_jwk, public_key_id, created_at: new Date().toISOString() };
}

// Generate a new identity. The private key is stored non-extractably in IndexedDB;
// only the public descriptor is returned (for localStorage). The extractable
// private JWK exists momentarily in memory to re-import as non-extractable, then
// is discarded — it is never persisted.
export async function generateIdentity(label) {
  const algorithm = await detectAlgo();
  const kp = await crypto.subtle.generateKey(keyParams(algorithm), true, ["sign", "verify"]);
  const public_jwk = await crypto.subtle.exportKey("jwk", kp.publicKey);
  const priv_jwk = await crypto.subtle.exportKey("jwk", kp.privateKey);
  const nonExtractable = await importNonExtractablePrivate(priv_jwk, algorithm);
  await idbPut(nonExtractable);
  const public_key_id = await thumbprint(public_jwk);
  return makeDescriptor(label, algorithm, public_jwk, public_key_id);
}

// A stored/loaded descriptor is valid if it carries public identity fields.
export function isValidIdentity(o) {
  return (
    !!o &&
    typeof o === "object" &&
    typeof o.algorithm === "string" &&
    !!o.public_jwk &&
    typeof o.public_key_id === "string"
  );
}

// Import an identity from a file. A full identity file (with private_jwk) is
// imported as a NON-EXTRACTABLE key into IndexedDB; the private JWK is NOT stored
// in localStorage. Returns the public-only descriptor, or null if invalid.
export async function importIdentity(obj) {
  if (!obj || typeof obj !== "object" || !obj.algorithm || !obj.public_jwk) return null;
  if (!obj.private_jwk) return null; // public-only file cannot sign
  try {
    const nonExtractable = await importNonExtractablePrivate(obj.private_jwk, obj.algorithm);
    await idbPut(nonExtractable);
  } catch {
    return null;
  }
  const public_key_id = obj.public_key_id || (await thumbprint(obj.public_jwk));
  return makeDescriptor(obj.label, obj.algorithm, obj.public_jwk, public_key_id);
}

export async function clearSigningKey() {
  await idbDelete();
}

export async function hasSigningKey() {
  const key = await idbGet();
  return !!key;
}

// Sign a hash string using the non-extractable key in IndexedDB.
export async function signHash(descriptor, hashHex) {
  const key = await idbGet();
  if (!key) throw new Error("No signing key available in secure key store");
  const data = new TextEncoder().encode(hashHex);
  const sig = await crypto.subtle.sign(signParams(descriptor.algorithm), key, data);
  return {
    algorithm: descriptor.algorithm,
    public_key_id: descriptor.public_key_id,
    public_jwk: descriptor.public_jwk, // PUBLIC ONLY
    signature: toB64(sig),
    signed_at: new Date().toISOString(),
    receipt_hash: hashHex,
  };
}

// Verify a signature against the receipt's CURRENT hash.
// States: SIGNATURE VERIFIED | SIGNATURE INVALID | UNSIGNED RECEIPT
export async function verifySignature(signature, currentHashHex) {
  if (!signature) return { state: "UNSIGNED RECEIPT", cryptoOk: false, hashMatch: false };
  try {
    const key = await importPubKey(signature.public_jwk, signature.algorithm);
    const data = new TextEncoder().encode(signature.receipt_hash);
    const cryptoOk = await crypto.subtle.verify(signParams(signature.algorithm), key, fromB64(signature.signature), data);
    const hashMatch = signature.receipt_hash === currentHashHex;
    return { state: cryptoOk && hashMatch ? "SIGNATURE VERIFIED" : "SIGNATURE INVALID", cryptoOk, hashMatch };
  } catch {
    return { state: "SIGNATURE INVALID", cryptoOk: false, hashMatch: false };
  }
}

// Public-only view for export (never contains the private key).
export function publicIdentity(identity) {
  return {
    label: identity.label,
    algorithm: identity.algorithm,
    public_jwk: identity.public_jwk,
    public_key_id: identity.public_key_id,
    created_at: identity.created_at,
  };
}
